import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EulaDialogComponent } from '@components';
import { EULA, EULAAcceptance } from '@models';
import { UserDataService } from '@services';
import { environment } from 'environments/environment';
import { Observable, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';

import { LoggerService } from '../logger/logger.service';

@Injectable({
  providedIn: 'root'
})
export class EulaService {
  private http: HttpClient = inject(HttpClient);
  private dialog: MatDialog = inject(MatDialog);
  private userDataService: UserDataService = inject(UserDataService);
  private logger = inject(LoggerService).setContext('EulaService');
  private eulaTitle = 'Terms of Use';
  BASE_URL = environment.CRG_DIGITALIZATION_SERVICE_URL;
  EULA_VALIDATION_URL = `${this.BASE_URL}/crg-sponsor-portal-service/api/v1/eula/validation`;
  EULA_ACCEPTANCE_URL = `${this.BASE_URL}/crg-sponsor-portal-service/api/v1/eula/accept`;

  checkUserEulaAcceptance(): Observable<EULA> {
    return this.prepareCommonParams().pipe(
      switchMap((commonParams) =>
        this.http.get<EULA>(this.EULA_VALIDATION_URL, {
          observe: 'response',
          params: commonParams,
          withCredentials: true
        })
      ),
      map((response) => (response as any).body as EULA),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('Error checking EULA acceptance:', error);
        return throwError(() => new Error('ErrorFetchingEulaAcceptance'));
      })
    );
  }

  saveEulaAcceptance(eulaId: number): Observable<EULAAcceptance> {
    return this.http
      .post(
        this.EULA_ACCEPTANCE_URL,
        { eula_id: eulaId },
        {
          observe: 'response',
          withCredentials: true
        }
      )
      .pipe(
        map((response) => response.body as EULAAcceptance),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('Error saving EULA acceptance:', error);
          return throwError(() => new Error('ErrorSavingEulaAcceptance'));
        })
      );
  }

  saveEulaAcceptanceFromGet(eulaId: number): Observable<EULAAcceptance> {
    return this.prepareCommonParams().pipe(
      map((commonParams) => commonParams.set('eula_id', eulaId.toString())),
      switchMap((commonParams) =>
        this.http.get(this.EULA_ACCEPTANCE_URL, {
          observe: 'response',
          params: commonParams,
          withCredentials: true
        })
      ),
      map((response) => (response as any).body as EULAAcceptance),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('Error saving EULA acceptance:', error);
        return throwError(() => new Error('ErrorSavingEulaAcceptance'));
      })
    );
  }

  showEulaDialog(eulaData: EULA, usePost = false): Observable<boolean> {
    const dialogRef: MatDialogRef<EulaDialogComponent, boolean> = this.dialog.open(
      EulaDialogComponent,
      {
        data: {
          eulaContent: eulaData.currentEula.documentContent,
          eulaTitle: this.eulaTitle,
          eulaId: eulaData.currentEula.id,
          usePost: usePost
        },
        disableClose: true
      }
    );

    return dialogRef.afterClosed().pipe(map((result) => result === true));
  }

  isCurrentEulaAccepted(eulaCheck: EULA): boolean {
    if (!eulaCheck.hasAcceptedCurrent) return false;
    this.setEulaAcceptanceInSession(eulaCheck.hasAcceptedCurrent);
    return true;
  }

  isCurrentEulaAcceptedInSession(): boolean {
    return this.getEulaAcceptanceFromSession();
  }

  storeEulaAcceptance(acceptance: boolean): void {
    this.setEulaAcceptanceInSession(acceptance);
  }

  private getEulaAcceptanceFromSession(): boolean {
    return sessionStorage.getItem('eulaAccepted') === 'true';
  }
  private setEulaAcceptanceInSession(acceptance: boolean): void {
    sessionStorage.setItem('eulaAccepted', acceptance ? 'true' : 'false');
  }

  private prepareCommonParams(): Observable<HttpParams> {
    return this.userDataService.isUserDataLoaded$.pipe(
      filter((loaded) => loaded === true),
      take(1),
      map(() => {
        const username = this.userDataService.getUsername();
        const principalId = this.userDataService.getPrincipalId();
        let params: HttpParams = new HttpParams();
        params = params.set('user_id', username ?? '');
        params = params.set('principal_id', principalId ?? '');
        return params;
      })
    );
  }
}
